import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

(() => {
  'use strict';

  if (window.__gmPotAddonOnlyInstalled) return;
  window.__gmPotAddonOnlyInstalled = true;

  const addingByComponent = new WeakSet();
  const handledPromises = new WeakSet();

  function normalizePot(raw) {
    if (!raw) return null;

    const variantId = Number(raw.variantId || raw.variant_id || raw.id);
    if (!Number.isFinite(variantId) || variantId <= 0) return null;

    return {
      variantId,
      potName: String(raw.potName || raw.name || raw.productTitle || '').trim(),
      colorName: String(raw.colorName || raw.color || raw.variantTitle || '').trim(),
      sizeCode: String(raw.sizeCode || raw.size || '').trim(),
    };
  }

  function getSelectedPot(plantVariantId) {
    const mode = window.gmPlantPotChoice?.mode || window.gmPotPresentation;
    if (mode !== 'decorative') return null;

    const pot = normalizePot(
      window.gmPlantPotChoice?.selection || window.gmSelectedPot
    );

    if (!pot || pot.variantId === plantVariantId) return null;
    return { ...pot };
  }

  function getPlantVariantId(component, event) {
    const eventVariantId = Number(event.lines?.[0]?.merchandiseId) || 0;
    if (eventVariantId > 0) return eventVariantId;

    return Number(component.querySelector('input[name="id"]')?.value) || 0;
  }

  function getQuantity(component, event) {
    const eventQuantity = Number(event.lines?.[0]?.quantity);
    if (Number.isFinite(eventQuantity) && eventQuantity > 0) {
      return Math.floor(eventQuantity);
    }

    const form = component.querySelector('form[action*="/cart/add"]');
    const formQuantity = form ? Number(new FormData(form).get('quantity')) : 0;

    return Math.max(
      1,
      Math.floor(formQuantity || Number(component.dataset.quantityDefault) || 1)
    );
  }

  function getSectionIds() {
    return [...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((element) => element.dataset.sectionId)
        .filter(Boolean)
    )];
  }

  async function fetchCart() {
    const cartUrl = window.Theme?.routes?.cart_url
      ? `${window.Theme.routes.cart_url}.js`
      : '/cart.js';

    const response = await fetch(cartUrl, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Could not refresh cart.');
    return response.json();
  }

  async function addPotOnly(component, pot, quantity) {
    if (addingByComponent.has(component)) return;
    addingByComponent.add(component);

    const properties = { '_GM pot add-on': 'true' };
    if (pot.potName) properties.Pot = pot.potName;
    if (pot.colorName) properties.Color = pot.colorName;
    if (pot.sizeCode) properties.Size = pot.sizeCode;

    const sectionIds = getSectionIds();
    const payload = {
      items: [{
        id: pot.variantId,
        quantity,
        properties,
      }],
    };

    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    const deferred = CartLinesUpdateEvent.createPromise();

    component.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{
        merchandiseId: String(pot.variantId),
        quantity,
      }],
      promise: deferred.promise,
    }));

    try {
      const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const response = await fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.status || result.errors) {
        throw new Error(
          result.description ||
          result.message ||
          result.errors ||
          'Could not add selected pot.'
        );
      }

      const cart = await fetchCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: result.sections || {},
          items: cart.items || [],
          itemCount: Number(cart.item_count) || 0,
          source: 'gm-pot-addon',
          sourceId: component.id || '',
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      console.error('[Green Mist pot add-on]', error);
    } finally {
      addingByComponent.delete(component);
    }
  }

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const component = event.target instanceof Element
      ? event.target.closest('product-form-component')
      : null;

    if (!component) return;
    if (addingByComponent.has(component)) return;
    if (event.action !== 'add' || event.context !== 'product') return;
    if (!event.promise || typeof event.promise.then !== 'function') return;
    if (handledPromises.has(event.promise)) return;

    const plantVariantId = getPlantVariantId(component, event);
    const pot = getSelectedPot(plantVariantId);
    if (!pot) return;

    const quantity = getQuantity(component, event);
    handledPromises.add(event.promise);

    event.promise
      .then(async (result) => {
        const detail = result?.detail || {};

        if (detail.didError) return;
        if (detail.source !== 'product-form-component') return;

        await addPotOnly(component, pot, quantity);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[Green Mist pot add-on] Plant add failed; pot was not added.',
            error
          );
        }
      });
  }, true);
})();
